import { Avatar, Badge, Box, Button, Card, Divider, Group, Loader, SimpleGrid, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { IconFileDescription, IconPhone, IconPlayerSkipForward, IconSparkles } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { PrepGuideError, PrepGuideSections } from "../candidates/PrepGuideSections";
import {
  EXPLORE_BRIEFING_REQUEST_EVENT,
  EXPLORE_CARD_VIEW_EVENT,
  interviewPrepQueryKey,
  PREP_BRIEFING_EMPTY,
  PREP_BRIEFING_ERROR,
  PREP_BRIEFING_LABEL,
  PREP_BRIEFING_LOADING,
  PREP_BRIEFING_REQUEST_LABEL,
  PREP_BRIEFING_RETRY,
} from "../../constants/recruiterAi";
import { candidatesApi, type QueueMatch } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { trackEvent } from "../../lib/observability";
import { formatPaise } from "../../lib/format";
import type { Job } from "../../types/api";
import { axioTokens } from "../../theme";
import {
  EXPLORE_EDUCATION_NOTES_LABEL,
  EXPLORE_FACT_GRID_COLS,
  EXPLORE_FACT_GRID_SPAN_ALL,
  EXPLORE_SKILL_FIT_LABEL,
  EXPLORE_SKILL_NONE_COPY,
  EXPLORE_SKILL_UNCONFIGURED_COPY,
  MISSING_SKILLS_VISIBLE,
} from "./constants";
import { candidateFacts, sourcingCopyFor, updatedAgoLabel } from "./exploreUtils";

function FactCell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap="var(--axio-unit-6)" miw="var(--axio-unit-0)">
      <Text size="xs" c="dimmed" tt="uppercase" fw="var(--axio-font-weight-semibold)">
        {label}
      </Text>
      {children}
    </Stack>
  );
}

export function ScoreBadge({ match }: { match: QueueMatch }) {
  const score = match.cached_score != null ? Number(match.cached_score) : null;
  return (
    <Text
      fw="var(--axio-font-weight-extra-bold)"
      lh="var(--axio-line-height-solid)"
      className="tabular"
      style={{ fontFamily: axioTokens.fontHead, fontSize: "var(--mantine-h1-font-size)" }}
    >
      {score != null ? score.toFixed(0) : "—"}
    </Text>
  );
}

export function CtcBadge({ match, job }: { match: QueueMatch; job: Job | undefined }) {
  const current = match.candidate?.current_ctc_paise;
  if (!job || current == null) return null;
  const ceiling = job.ctc_max_paise ?? job.ctc_min_paise;
  const overBudget = ceiling != null && current > ceiling;
  const budgetLabel =
    job.ctc_min_paise != null && job.ctc_max_paise != null
      ? `${formatPaise(job.ctc_min_paise)}-${formatPaise(job.ctc_max_paise)}`
      : ceiling != null
        ? formatPaise(ceiling)
        : null;
  return (
    <Badge color={overBudget ? "orange" : "teal"} variant="light" className="tabular">
      Current {formatPaise(current)}
      {budgetLabel ? ` · Budget ${budgetLabel}` : ""}
    </Badge>
  );
}

export function SkillFitBadges({ match }: { match: QueueMatch }) {
  const [showAllMissing, setShowAllMissing] = useState(false);
  const fit = match.skill_fit;
  if (!fit || (fit.required?.length ?? 0) === 0) {
    return (
      <FactCell label={EXPLORE_SKILL_FIT_LABEL}>
        <Text size="sm" c="dimmed">
          {EXPLORE_SKILL_UNCONFIGURED_COPY}
        </Text>
      </FactCell>
    );
  }
  const missing = [
    ...fit.missing_must.map((skill) => ({ skill, must: true })),
    ...fit.missing_nice.map((skill) => ({ skill, must: false })),
  ];
  const extraMissingCount = Math.max(0, missing.length - MISSING_SKILLS_VISIBLE);
  const shownMissing = showAllMissing ? missing : missing.slice(0, MISSING_SKILLS_VISIBLE);
  const hasBadges = fit.matched.length > 0 || missing.length > 0;
  return (
    <FactCell label={EXPLORE_SKILL_FIT_LABEL}>
      {hasBadges ? (
        <Group gap="var(--axio-unit-6)" wrap="wrap">
          {fit.matched.map((s) => (
            <Badge key={`m-${s}`} size="sm" color="teal" variant="light">
              {s}
            </Badge>
          ))}
          {shownMissing.map((item) =>
            item.must ? (
              <Badge key={`mm-${item.skill}`} size="sm" color="red" variant="filled">
                {item.skill} (must)
              </Badge>
            ) : (
              <Badge key={`mn-${item.skill}`} size="sm" color="orange" variant="light">
                {item.skill}
              </Badge>
            ),
          )}
          {!showAllMissing && extraMissingCount > 0 && (
            <UnstyledButton onClick={() => setShowAllMissing(true)}>
              <Badge size="sm" color="gray" variant="light">
                +{extraMissingCount} more
              </Badge>
            </UnstyledButton>
          )}
        </Group>
      ) : (
        <Text size="sm" c="dimmed">
          {EXPLORE_SKILL_NONE_COPY}
        </Text>
      )}
    </FactCell>
  );
}

export function CallBriefingCallout({
  isLoading,
  isError,
  summary,
  onRetry,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  summary?: string | null;
  onRetry: () => void;
  children?: ReactNode;
}) {
  return (
    <Group wrap="nowrap" gap="var(--axio-unit-0)" align="stretch">
      <Box
        style={{
          width: axioTokens.spacing.xs,
          background: axioTokens.violet,
          flexShrink: "var(--axio-number-zero)",
        }}
      />
      <Stack gap="md" p="md" style={{ flex: "var(--axio-number-one)", background: axioTokens.violetLight }}>
        <Group gap="var(--axio-unit-6)">
          <IconSparkles size="var(--axio-unit-16)" color={axioTokens.violet} />
          <Text size="xs" fw="var(--axio-font-weight-bold)" c="violet">
            {PREP_BRIEFING_LABEL}
          </Text>
        </Group>
        {isLoading ? (
          <Group gap="xs">
            <Loader size="xs" color="violet" />
            <Text size="sm" c="dimmed">
              {PREP_BRIEFING_LOADING}
            </Text>
          </Group>
        ) : isError ? (
          <Group gap="xs" wrap="wrap">
            <Text size="sm">{PREP_BRIEFING_ERROR}</Text>
            <Button variant="subtle" size="compact-sm" onClick={onRetry}>
              {PREP_BRIEFING_RETRY}
            </Button>
          </Group>
        ) : children ? (
          children
        ) : (
          <Text size="sm" lh="var(--axio-line-height-body)">
            {summary?.trim() || PREP_BRIEFING_EMPTY}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

function DevScoreBreakdown({ match }: { match: QueueMatch }) {
  const b = match.score_breakdown;
  if (!import.meta.env.DEV || !b) return null;
  return (
    <Text size="xs" c="dimmed">
      Skills {b.skill ?? "—"} · Exp {b.experience ?? "—"} · Fresh {b.freshness ?? "—"} · Eng{" "}
      {b.engagement ?? "—"} · Comp {b.ctc_fit ?? "—"} · Loc {b.location_fit ?? "—"} · Avail{" "}
      {b.availability_fit ?? "—"}
      {b.contact_penalty ? ` · −${b.contact_penalty}` : ""}
      {b.vector_cosine != null ? ` · Vec ${Number(b.vector_cosine).toFixed(2)}` : ""}
    </Text>
  );
}

export function MatchCard({
  match,
  job,
  onCall,
  calling,
  dimmed,
  callLocked,
  detailed,
  saveForwardLabel,
  saveForwardDisabled,
  saveForwardLoading,
  passLoading,
  onSaveForward,
  onPass,
}: {
  match: QueueMatch;
  job?: Job;
  onCall: (m: QueueMatch) => void;
  calling?: boolean;
  dimmed?: boolean;
  callLocked?: boolean;
  detailed?: boolean;
  saveForwardLabel?: string;
  saveForwardDisabled?: boolean;
  saveForwardLoading?: boolean;
  passLoading?: boolean;
  onSaveForward?: (m: QueueMatch) => void;
  onPass?: (m: QueueMatch) => void;
}) {
  const [briefingOpen, setBriefingOpen] = useState(false);
  const prepQuery = useQuery({
    queryKey: interviewPrepQueryKey(match.candidate_id, match.job_id),
    queryFn: () => candidatesApi.interviewPrep(match.candidate_id, { job_id: match.job_id }),
    enabled: briefingOpen,
    retry: false,
  });
  useEffect(() => {
    trackEvent(EXPLORE_CARD_VIEW_EVENT, {
      detailed: Boolean(detailed),
      match_id: match.id,
    });
  }, [detailed, match.id]);
  function requestBriefing() {
    trackEvent(EXPLORE_BRIEFING_REQUEST_EVENT, {
      detailed: Boolean(detailed),
      match_id: match.id,
    });
    setBriefingOpen(true);
  }
  const prepError =
    prepQuery.error instanceof ApiError
      ? prepQuery.error.message
      : prepQuery.isError
        ? PREP_BRIEFING_ERROR
        : null;
  const c = match.candidate;
  if (!c) return null;
  const originCopy = sourcingCopyFor(match);
  const sourcingPct = job?.sourcing_fee_pct;
  const updatedLabel = updatedAgoLabel(c.profile_updated_at ?? c.created_at);
  const facts = detailed ? candidateFacts(c) : [];
  return (
    <Card
      withBorder
      padding="xl"

      role="group"
      aria-label={`Candidate match: ${c.full_name}`}
      opacity={dimmed ? axioTokens.opacity.dimmed : axioTokens.opacity.visible}
      style={{ borderColor: axioTokens.border }}
    >
      <Stack gap={detailed ? axioTokens.unit[32] : "md"}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="lg" wrap="nowrap">
            <Avatar radius={axioTokens.radiusCircle} size="lg" color="violet" variant="light">
              {c.full_name.slice(0, 1).toUpperCase()}
            </Avatar>
            <Stack gap="var(--axio-unit-6)">
              <Group gap="xs" wrap="wrap" align="baseline">
                <Text fw="var(--axio-font-weight-extra-bold)" size="lg" lh="var(--axio-line-height-dense)">{c.full_name}</Text>
                {match.callable_profile === false && <Badge color="orange" variant="light">Needs details</Badge>}
              </Group>
              {c.current_title && (
                <Tooltip label="This was the candidate’s last designation." withArrow>
                  <Text size="sm" fw="var(--axio-font-weight-semibold)" lh="var(--axio-line-height-compact)" style={{ width: "fit-content" }}>
                    {c.current_title}
                  </Text>
                </Tooltip>
              )}
              {updatedLabel && (
                <Text size="xs" c="dimmed">
                  {updatedLabel}
                </Text>
              )}
            </Stack>
          </Group>
          <Stack align="flex-end" gap="var(--axio-unit-2)">
            <ScoreBadge match={match} />
            <Text size="xs" c="dimmed">
              {detailed ? "Start here" : "Match score"}
            </Text>
          </Stack>
        </Group>

        <Stack gap="md">
          {detailed && !briefingOpen && (
            <Button
              variant="subtle"
              color="violet"
              className="exploreAiLink"
              leftSection={<IconSparkles size="var(--axio-unit-16)" />}
              disabled={dimmed}
              onClick={requestBriefing}
            >
              {PREP_BRIEFING_REQUEST_LABEL}
            </Button>
          )}
          {briefingOpen && (
            <CallBriefingCallout
              isLoading={prepQuery.isLoading && !prepQuery.data}
              isError={prepQuery.isError && !prepQuery.data}
              onRetry={() => void prepQuery.refetch()}
            >
              {prepQuery.data ? (
                <Stack gap="md">
                  {prepError ? <PrepGuideError message={prepError} /> : null}
                  <PrepGuideSections prep={prepQuery.data} compact />
                </Stack>
              ) : null}
            </CallBriefingCallout>
          )}
          <CtcBadge match={match} job={job} />
          {detailed ? (
            <SimpleGrid type="container" cols={EXPLORE_FACT_GRID_COLS} spacing="lg">
              <Box style={{ gridColumn: EXPLORE_FACT_GRID_SPAN_ALL }}>
                <SkillFitBadges match={match} />
              </Box>
              {facts.map((fact) => (
                <FactCell key={fact.label} label={fact.label}>
                  <Text size="sm">{fact.value}</Text>
                </FactCell>
              ))}
              {c.education_summary && (
                <FactCell label={EXPLORE_EDUCATION_NOTES_LABEL}>
                  <Text size="sm">{c.education_summary}</Text>
                </FactCell>
              )}
            </SimpleGrid>
          ) : (
            <SkillFitBadges match={match} />
          )}
        </Stack>

        <Group justify="space-between" align="flex-end" pt="lg" wrap="wrap" gap="md" style={{ borderTop: `${axioTokens.unit[1]}px solid ${axioTokens.border}` }}>
          <Stack gap="var(--axio-unit-6)" style={{ flex: "1 1 auto" }}>
            {match.skill_fit && !match.skill_fit.has_all_must && (
              <Text size="sm" c="red.7" fw="var(--axio-font-weight-semibold)">Review missing must-have skills before calling</Text>
            )}
            <Text size="sm" fw="var(--axio-font-weight-semibold)">
              {originCopy.label}
            </Text>
            {sourcingPct != null && (
              <Text size="sm" c="dimmed">
                {sourcingPct}% sourcing fee goes to {originCopy.feeGoesTo}
              </Text>
            )}
          </Stack>
          <Group gap="sm" wrap="wrap">
            {c.resume_url && (
              <Button component="a" href={c.resume_url} target="_blank" rel="noopener noreferrer" variant="subtle" color="dark" leftSection={<IconFileDescription size="var(--axio-unit-16)" />} disabled={dimmed}>
                Resume
              </Button>
            )}
            {!detailed && (
              <Button
                variant="subtle"
                color="violet"
                className="exploreAiLink"
                leftSection={<IconSparkles size="var(--axio-unit-16)" />}
                disabled={dimmed}
                loading={briefingOpen && prepQuery.isFetching}
                onClick={requestBriefing}
              >
                {PREP_BRIEFING_LABEL}
              </Button>
            )}
            {onPass && (
              <Button
                variant="default"
                leftSection={<IconPlayerSkipForward size="var(--axio-unit-16)" />}
                onClick={() => onPass(match)}
                loading={passLoading}
                disabled={dimmed || callLocked}
              >
                Pass
              </Button>
            )}
            {onSaveForward && (
              <Button
                variant="default"
                onClick={() => onSaveForward(match)}
                loading={saveForwardLoading}
                disabled={dimmed || callLocked || saveForwardDisabled}
              >
                {saveForwardLabel ?? "Save forward"}
              </Button>
            )}
            <Button leftSection={<IconPhone size="var(--axio-unit-16)" />} onClick={() => onCall(match)} loading={calling} disabled={dimmed || callLocked} color="violet">
              Call
            </Button>
          </Group>
        </Group>
        {import.meta.env.DEV && match.score_breakdown && (
          <Stack gap="md">
            <Divider color={axioTokens.border} />
            <DevScoreBreakdown match={match} />
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
